import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { css, keyframes } from '@emotion/core';
import IconButton from '@material-ui/core/IconButton';

import { getIconByType } from 'common/utils/config';
import { below } from 'common/utils/breakpoint';

export default function Rating(props) {
    const {
        range = 5,
        iconType,
        value,
        onChange = () => {},
        disabled,
        style,
        customIconUrl,
        customSelectedIconUrl
    } = props;
    const [selectCounter, setSelectCounter] = useState(0);
    const [shouldAnimate, setShouldAnimate] = useState(false);
    const [hoveredIndex, setHoveredIndex] = useState(0);
    const [Hollow, Solid] = getIconByType(iconType);
    useEffect(() => {
        if (selectCounter !== 0) {
            setShouldAnimate(true);
        }
    }, [selectCounter]);
    return (
        <div css={wrapperStyle(range)}>
            {new Array(range).fill(0).map((v, index) => {
                const num = index + 1;
                function onClick() {
                    if (!disabled) {
                        setSelectCounter(current => current + 1);
                        onChange(num);
                    }
                }
                const shouldBlink = shouldAnimate && num === value;
                const styles = !disabled
                    ? [
                          (hoveredIndex !== 0 ? num <= hoveredIndex : num <= value) && fillStyle,
                          hoveredIndex !== 0 && num === hoveredIndex && scaleStyle,
                          shouldBlink && blinkStyle
                      ]
                    : [];
                return (
                    <div className="item" key={index} css={style}>
                        <div className="iconWrapper">
                            <IconButton
                                disableRipple
                                onClick={onClick}
                                css={[iconStyle, ...styles]}
                                onMouseEnter={() => {
                                    setHoveredIndex(num);
                                    setShouldAnimate(false);
                                }}
                                onMouseLeave={() => {
                                    if (!shouldBlink) {
                                        setHoveredIndex(0);
                                    }
                                }}
                                onAnimationEnd={() => {
                                    if (shouldBlink) {
                                        setHoveredIndex(0);
                                        setShouldAnimate(false);
                                    }
                                }}
                            >
                                {iconType === -1 ? (
                                    <Fragment>
                                        {customIconUrl ? (
                                            <img
                                                src={customIconUrl}
                                                css={fullHeightWidhtStyle}
                                                alt="icon"
                                                className="icon"
                                            />
                                        ) : (
                                            <span
                                                css={[fullHeightWidhtStyle, placeholderStyle]}
                                                className="icon"
                                            />
                                        )}
                                        {customSelectedIconUrl ? (
                                            <img
                                                src={customSelectedIconUrl}
                                                css={fullHeightWidhtStyle}
                                                alt="icon-selected"
                                                className="icon-selected"
                                            />
                                        ) : (
                                            <span
                                                css={[
                                                    fullHeightWidhtStyle,
                                                    placeholderSelectedStyle
                                                ]}
                                                className="icon-selected"
                                            />
                                        )}
                                    </Fragment>
                                ) : (
                                    <Fragment>
                                        <Hollow className="icon" />
                                        <Solid className="icon-selected" />
                                    </Fragment>
                                )}
                            </IconButton>
                        </div>
                        <div className="title">{num}</div>
                    </div>
                );
            })}
        </div>
    );
}

Rating.propTypes = {
    range: PropTypes.number,
    value: PropTypes.number,
    iconType: PropTypes.number,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    style: PropTypes.object,
    customIconUrl: PropTypes.string,
    customSelectedIconUrl: PropTypes.string
};

const blink = keyframes`
    from, 20%, 60%, 90% {
        opacity: 0;
    }
    10%, 50%, 70%, to {
        opacity: 1;
    }
`;

const blinkStyle = css`
    & *.icon-selected {
        animation: ${blink} 0.7s ease-out 1;
    }
`;

const iconStyle = css`
    color: inherit;
    & *.icon,
    & *.icon-selected {
        transition: all 0.2s linear;
    }
    & *.icon-selected {
        display: none;
    }
`;

const fillStyle = css`
    & *.icon {
        display: none;
    }
    & *.icon-selected {
        display: block;
    }
`;

const scaleStyle = css`
    transform: scale(1.1);
`;

const fullFixed = `
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
`;

const fullHeightWidhtStyle = css`
    width: 100%;
    height: 100%;
    box-sizing: border-box;
`;

const placeholderSelectedStyle = css`
    background-color: #ccc;
    border-radius: 50%;
`;

const placeholderStyle = css`
    border-radius: 50%;
    border: 2px solid #ccc;
`;

const wrapperStyle = range => {
    const iconDesktopWidth = 10;
    const iconMobileWidth = range <= 5 ? 20 : 100 / range;
    const iconMobilePadding = range <= 5 ? 12 : 4;
    return css`
        position: relative;

        > .item {
            display: inline-block;
            width: ${iconDesktopWidth}%;

            ${below('xsm')} {
                width: ${iconMobileWidth}%;
            }

            > .iconWrapper {
                position: relative;
                width: 100%;
                padding-top: 100%;

                > .MuiIconButton-root {
                    ${fullFixed}
                    padding: 0;
                    box-sizing: border-box;
                    &:hover {
                        background-color: transparent;
                    }

                    > .MuiIconButton-label {
                        ${fullFixed}
                        padding: 8px;
                        box-sizing: border-box;

                        ${below('xsm')} {
                            padding: ${iconMobilePadding}px;
                        }

                        > svg {
                            width: 100%;
                            height: 100%;
                        }
                    }
                }
            }

            > .title {
                text-align: center;
            }
        }
    `;
};
